import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconKey } from '@tabler/icons-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppNotification } from '@feature/utils';

import { useUserTemporaryPasswordReissue } from '../model/UserQuery';
import { User } from '../model/UserTypes';

interface LocalAccountReissueButtonProps {
  user: User;
}

export function LocalAccountReissueButton({ user }: LocalAccountReissueButtonProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [opened, { open, close }] = useDisclosure(false);
  const mutation = useUserTemporaryPasswordReissue(user.id);

  // Two clicks in one frame both see isPending false, so the loading button alone lets the second one through.
  const inFlight = useRef(false);

  const onConfirm = () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    mutation.mutate(undefined, {
      onError: error => {
        notification.error(error);
        close();
      },
      onSuccess: () => {
        notifications.show({
          color: 'green',
          title: t('notification.success.title'),
          message: t('localAccounts.reissue.success'),
        });
        close();
      },
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <>
      <Button
        size="xs"
        variant="outline"
        leftSection={<IconKey size={14} />}
        onClick={open}
        aria-label={`${t('localAccounts.reissue.action')}: ${user.email}`}
      >
        {t('localAccounts.reissue.action')}
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={t('localAccounts.reissue.confirmTitle')}
        centered
      >
        <Stack>
          <Text fw="500">{t('localAccounts.reissue.confirmMessage', { email: user.email })}</Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={close}
              loading={mutation.isPending}
            >
              {t('button.cancel')}
            </Button>
            <Button
              onClick={onConfirm}
              loading={mutation.isPending}
            >
              {t('localAccounts.reissue.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
